import React, { useLayoutEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { basePageWrap } from '../BasePage';
import styles from './ArticlePage.module.scss';
import isEmpty from '../../utils/isEmpty';
import { CookieStateContext } from '../BasePage/state';
import Hero from '../../components/Hero';
import BookmarkButton from '../../components/BookmarkButton';
import ShareFeedbackSegment from '../../components/ShareFeedbackSegment';
import StreamField from '../../components/StreamField';
import Breadcrumbs from '../../components/Breadcrumbs';
import AuthorCard from '../../components/AuthorCard';
import LinkList from '../../components/LinkList';
import Sponsors from '../../components/Sponsors';
import PartnerInformation from '../../components/PartnerInformation';
import BackBanner from '../../components/BackBanner';
import MapExpander from '../../components/MapExpander';
import ArticleDate from '../../components/ArticleDate';

const ArticlePage = ({
    id = 0,
    url = '',
    title = '',
    contentBlocks = [],
    hero = {},
    breadcrumbs = {},
    preamble = '',
    linksDocumentsBlock = [],
    sponsored = {},
    author = {},
    map = {},
    partner = {},
    partOfPage = {},
    backToMap = false,
    backToWhatToDo = {},
    date = {},
}) => {
    const { t } = useTranslation();

    const hasMapBlock = contentBlocks.find((content) => content.type === 'map');
    const blocks = contentBlocks.map((content) =>
        content.type === 'map'
            ? {
                  ...content,
                  value: {
                      items: map.coords.map((item) => ({
                          ...item,
                          readMore: item?.url ? null : t('mapExpander.goToMap'),
                          href:
                              item?.url ||
                              `https://maps.google.com/maps/dir/${item.lat},${item.lng}/@${item.lat},${item.lng},16z`,
                      })),
                      apiKey: map.googleMapsApiKey,
                  },
              }
            : content
    );

    const { state } = useContext(CookieStateContext) || {};
    const cookieConsent = state?.categories?.includes('functionality');

    useLayoutEffect(() => {
        if (
            typeof window !== 'undefined' &&
            window.location.search.indexOf('from_map') > -1
        ) {
            window.history.replaceState({}, '', window.location.pathname);
        }
        if (
            typeof window !== 'undefined' &&
            window.location.search.indexOf('what_to_do') > -1
        ) {
            window.history.replaceState('', '', window.location.pathname);
        }
    }, []);

    hero = {
        ...hero,
        isBelowBreadcrumbs:
            breadcrumbs && breadcrumbs.items && breadcrumbs.items.length > 1,
    };

    const bookmark = {
        id,
        url,
    };

    const classes = classNames(styles['ArticlePage'], {
        [styles['ArticlePage--HasMap']]: map && !isEmpty(map.coords),
        [styles['ArticlePage--Imageless']]: hero && isEmpty(hero.image),
    });

    const hasMapExpander = !isEmpty(map.coords);

    return (
        <div className={classes}>
            <Breadcrumbs {...breadcrumbs} />
            <Hero {...hero} clean={true} />
            <div className={styles['ArticlePage__Main']}>
                <div id="main-content" />
                <div className={styles['ArticlePage__Container']}>
                    <h1 className={styles['ArticlePage__Title']}>{title}</h1>
                    {preamble && (
                        <div
                            className={styles['ArticlePage__Preamble']}
                            dangerouslySetInnerHTML={{ __html: preamble }}
                        />
                    )}

                    {date && <ArticleDate {...date} />}
                </div>

                <div className={styles['ArticlePage__Actions']}>
                    {cookieConsent && (
                        <BookmarkButton
                            untranslatedButtonModifiers={
                                hasMapExpander
                                    ? ['Button--WithMapExpanderHeight']
                                    : []
                            }
                            {...bookmark}
                        />
                    )}
                    {hasMapExpander && (
                        <MapExpander
                            googleMapsApiKey={map.googleMapsApiKey}
                            items={map.coords}
                            zoomLevel={map.zoomLevel}
                        />
                    )}
                </div>

                {!isEmpty(partOfPage) && partOfPage.href && (
                    <BackBanner
                        backToMap={false}
                        text={partOfPage.title}
                        href={partOfPage.href}
                    />
                )}

                {backToWhatToDo && !isEmpty(backToWhatToDo) && (
                    <BackBanner
                        backToMap={false}
                        text={backToWhatToDo.title}
                        href={backToWhatToDo.href}
                    />
                )}
                {backToMap && <BackBanner backToMap={true} />}

                {linksDocumentsBlock && linksDocumentsBlock.length > 0 && (
                    <div
                        className={
                            styles['ArticlePage__Links'] +
                            ' ' +
                            styles['ArticlePage__Links--Desktop']
                        }>
                        <LinkList {...linksDocumentsBlock[0].value} />
                    </div>
                )}
            </div>

            {Array.isArray(blocks) && (
                <StreamField items={blocks} pageType={'Article'} />
            )}

            {linksDocumentsBlock && linksDocumentsBlock.length > 0 && (
                <div className={styles['ArticlePage__Container']}>
                    <div
                        className={
                            styles['ArticlePage__Links'] +
                            ' ' +
                            styles['ArticlePage__Links--Mobile']
                        }>
                        <LinkList {...linksDocumentsBlock[0].value} />
                    </div>
                </div>
            )}

            {!isEmpty(author) && <AuthorCard {...author} />}

            {!isEmpty(partner) && !isEmpty(partner.url) && (
                <PartnerInformation {...partner} />
            )}

            <ShareFeedbackSegment bookmark={bookmark} articleId={id} />

            {!isEmpty(sponsored) && <Sponsors {...sponsored} />}
        </div>
    );
};

ArticlePage.propTypes = {
    id: PropTypes.number.isRequired,
    url: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    preamble: PropTypes.string.isRequired,
    backToWhatToDo: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    hero: PropTypes.object,
    map: PropTypes.object,
    breadcrumbs: PropTypes.object,
    contentBlocks: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    linksDocumentsBlock: PropTypes.array,
    sponsored: PropTypes.object,
    partner: PropTypes.object,
    author: PropTypes.object,
    partOfPage: PropTypes.object,
    date: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    backToMap: PropTypes.bool,
};

export default basePageWrap(ArticlePage);
